import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Upload, Trash2, Copy, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "../../lib/errors";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingAlt, setEditingAlt] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/media");
      setItems(res.data);
    } catch { toast.error("Could not load media"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: exceeds 5MB`); continue; }
      try {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setItems(prev => [res.data, ...prev]);
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(extractErrorMessage(err, `Upload failed: ${file.name}`));
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this image? Pages or posts using it will lose the image.")) return;
    try {
      await api.delete(`/admin/media/${id}`);
      setItems(prev => prev.filter(x => x.media_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const saveAlt = async (id) => {
    try {
      await api.patch(`/admin/media/${id}`, { alt: editingAlt[id] ?? "" });
      setItems(prev => prev.map(x => x.media_id === id ? { ...x, alt: editingAlt[id] ?? "" } : x));
      setEditingAlt(prev => { const c = { ...prev }; delete c[id]; return c; });
      toast.success("Alt text saved");
    } catch { toast.error("Save failed"); }
  };

  const copyUrl = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.media_id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* noop */ }
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Media library</h1>
          <p className="text-sm text-[#4B5563]">Upload images here, then reference them from blog posts or page sections.</p>
        </div>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="media-page-upload-btn">
          <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload images"}
        </Button>
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onUpload} />
      </div>

      <div className="mt-6">
        {loading && <div className="text-[#9CA3AF] text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="bg-white border border-dashed border-black/15 rounded-2xl p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#37B160]/10 grid place-items-center">
              <Upload className="h-6 w-6 text-[#37B160]" />
            </div>
            <p className="mt-4 text-[#4B5563]">No images yet — upload your first one to get started.</p>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(item => (
            <div key={item.media_id} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`media-row-${item.media_id}`}>
              <div className="aspect-[4/3] bg-[#F3F4F6] relative">
                <img src={item.url} alt={item.alt || item.original_filename} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-xs text-[#4B5563] truncate" title={item.original_filename}>{item.original_filename}</div>
                <div className="text-[10px] text-[#9CA3AF] mt-0.5">{(item.size / 1024).toFixed(0)} KB</div>
                {editingAlt[item.media_id] !== undefined ? (
                  <div className="flex gap-1 mt-2">
                    <Input value={editingAlt[item.media_id]} onChange={e => setEditingAlt(prev => ({ ...prev, [item.media_id]: e.target.value }))} placeholder="Alt text" className="h-8 text-xs" />
                    <Button size="sm" onClick={() => saveAlt(item.media_id)} className="bg-[#37B160] hover:bg-[#2D9450] text-white h-8 px-2 text-xs">Save</Button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setEditingAlt(prev => ({ ...prev, [item.media_id]: item.alt || "" }))} className="mt-2 text-xs text-[#4B5563] hover:text-[#111827] inline-flex items-center gap-1" data-testid={`media-alt-edit-${item.media_id}`}>
                    <Pencil className="h-3 w-3" /> {item.alt ? item.alt : "Add alt text"}
                  </button>
                )}
                <div className="flex items-center gap-1 mt-3">
                  <button type="button" onClick={() => copyUrl(item)} className="flex-1 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#111827] text-xs px-2 py-1.5 rounded inline-flex items-center justify-center gap-1" data-testid={`media-copy-${item.media_id}`}>
                    {copiedId === item.media_id ? <><Check className="h-3 w-3" /> Copied URL</> : <><Copy className="h-3 w-3" /> Copy URL</>}
                  </button>
                  {confirmDeleteId === item.media_id ? (
                    <>
                      <button type="button" disabled={deletingId === item.media_id}
                              onClick={() => onDelete(item.media_id)}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1.5 rounded font-medium"
                              data-testid={`media-delete-confirm-${item.media_id}`}>
                        {deletingId === item.media_id ? "Deleting…" : "Yes, delete"}
                      </button>
                      <button type="button" onClick={() => setConfirmDeleteId(null)}
                              className="bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#4B5563] text-xs px-2 py-1.5 rounded"
                              data-testid={`media-delete-cancel-${item.media_id}`}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setConfirmDeleteId(item.media_id)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 text-xs px-2 py-1.5 rounded"
                            title="Delete image" data-testid={`media-delete-${item.media_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
